import { TRANSPORT_EMAIL_MAX, TRANSPORT_NAME_MAX, type TransportProvider, makeCreateTransportProviderRequest, makePatchTransportProviderRequest } from "@seagames/contracts";
import { Field, Modal, ModalActions, Select } from "@seagames/ui";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { conflictField } from "../common/apply-api-error";
import type { ApiError } from "../../lib/api/client";
import { createProvider, invalidateFleet, updateProvider } from "../../lib/api/transport-fleet";
import { toast } from "../../lib/ui/toast";
import { inputClass, routeSaveError, useProblemText } from "./form-bits";
import { useCallingCodes } from "./hooks";
import { useTransportFleetFormMessages as useFleetFormMessages } from "../../lib/i18n/use-form-schemas";
import { type ProviderDraft, type ProviderField, type Problems, checkProvider, emptyProviderDraft, hasProblems, issuesToProblems, providerBody, providerDraftFrom, splitE164 } from "./model";

export type ProviderFormTarget = { mode: "create" } | { mode: "edit"; provider: TransportProvider };

const KNOWN: readonly ProviderField[] = ["name", "contact_name", "number", "email", "is_active"];

/**
 * A transport company (SUPER_ADMIN): name, the person the desk calls, and
 * whether it is in service. Its vehicles and drivers name it; its logins
 * reach it through a Transport provider user group's facility row.
 */
export function ProviderFormModal({ target, onClose }: { target: ProviderFormTarget | null; onClose: () => void }) {
	if (!target) return null;
	return target.mode === "create" ? <ProviderForm onClose={onClose} /> : <ProviderForm key={target.provider.transport_provider_id} before={target.provider} onClose={onClose} />;
}

function ProviderForm({ before, onClose }: { before?: TransportProvider; onClose: () => void }) {
	const { t } = useTranslation("transportFleet");
	const { t: tc } = useTranslation("common");
	const problemText = useProblemText();
	const callingCodes = useCallingCodes();
	const messages = useFleetFormMessages();
	const [draft, setDraft] = useState<ProviderDraft>(() => (before ? providerDraftFrom(before, callingCodes) : emptyProviderDraft()));
	const [problems, setProblems] = useState<Problems<ProviderField>>({});
	const [busy, setBusy] = useState(false);
	const firstField = useRef<HTMLInputElement>(null);

	const split = useRef(callingCodes.length > 0);
	useEffect(() => {
		if (!before?.contact_phone || split.current || callingCodes.length === 0) return;
		split.current = true;
		const [dial, number] = splitE164(before.contact_phone, callingCodes);
		setDraft((d) => ({ ...d, dial: dial || "60", number }));
	}, [before, callingCodes]);

	const set = <K extends ProviderField>(k: K, v: ProviderDraft[K]) => {
		setDraft((d) => ({ ...d, [k]: v }));
		setProblems((p) => (p[k] ? { ...p, [k]: undefined } : p));
	};

	const special = (err: ApiError): Problems<ProviderField> | null => (conflictField(err) === "name" ? { name: { key: "companyNameTaken" } } : null);

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		const found = checkProvider(draft);
		setProblems(found);
		if (hasProblems(found)) return;
		setBusy(true);
		try {
			const body = providerBody(draft);
			const rename = { contact_phone: "number" as const };
			if (!before) {
				const parsed = makeCreateTransportProviderRequest(messages).safeParse(body);
				if (!parsed.success) {
					setProblems(issuesToProblems(parsed.error.issues, KNOWN, "name", rename));
					return;
				}
				await createProvider(parsed.data);
				toast.success(t("providerForm.toast.added", { name: body.name }));
			} else {
				const was = providerBody(providerDraftFrom(before, callingCodes));
				const patch = Object.fromEntries(Object.entries(body).filter(([k, v]) => v !== was[k as keyof typeof was]));
				if (Object.keys(patch).length === 0) {
					onClose();
					return;
				}
				const changed = Object.keys(patch).map((k) => k.replace(/_/g, " "));
				const parsed = makePatchTransportProviderRequest(messages).safeParse({ ...patch, reason: `Changed ${changed.join(", ")} for transport company ${before.name}` });
				if (!parsed.success) {
					setProblems(issuesToProblems(parsed.error.issues, KNOWN, "name", rename));
					return;
				}
				await updateProvider(before.transport_provider_id, parsed.data);
				toast.success(t("providerForm.toast.updated", { name: body.name }));
			}
			await invalidateFleet();
			onClose();
		} catch (err) {
			routeSaveError(err, KNOWN, setProblems, before ? t("providerForm.toast.updateFailed") : t("providerForm.toast.addFailed"), special);
		} finally {
			setBusy(false);
		}
	};

	const idp = before ? "tp-edit" : "tp-add";
	const formId = `${idp}-form`;

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			title={before ? before.name : t("providerForm.addTitle")}
			description={before ? undefined : t("providerForm.addDescription")}
			dismissible={!busy}
			initialFocusRef={firstField}
			footer={
				<ModalActions>
					<button type="button" className="btn btn-line btn-sm" onClick={onClose} disabled={busy}>
						{tc("action.cancel")}
					</button>
					<button type="submit" form={formId} className="btn btn-primary btn-sm" disabled={busy}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{t("providerForm.save")}
					</button>
				</ModalActions>
			}
		>
			<form id={formId} onSubmit={(e) => void submit(e)} noValidate className="grid gap-[0.75rem] sm:grid-cols-2">
				<Field label={t("providerForm.name")} htmlFor={`${idp}-name`} required error={problemText(problems.name)} className="sm:col-span-2">
					<input ref={firstField} id={`${idp}-name`} value={draft.name} onChange={(e) => set("name", e.target.value)} autoComplete="off" maxLength={TRANSPORT_NAME_MAX} className={inputClass(!!problems.name)} />
				</Field>
				<Field label={t("providerForm.contactName")} htmlFor={`${idp}-contact`} aside={tc("state.optional")} error={problemText(problems.contact_name)} className="sm:col-span-2">
					<input id={`${idp}-contact`} value={draft.contact_name} onChange={(e) => set("contact_name", e.target.value)} autoComplete="off" maxLength={TRANSPORT_NAME_MAX} className={inputClass(!!problems.contact_name)} />
				</Field>
				<Field label={t("providerForm.phone")} htmlFor={`${idp}-phone`} aside={tc("state.optional")} error={problemText(problems.number)}>
					<div className="grid grid-cols-[5.75rem_minmax(0,1fr)] gap-[0.5rem]">
						<Select value={draft.dial || null} onChange={(v) => set("dial", v ?? "")} options={callingCodes.map((c) => ({ value: c, label: `+${c}` }))} placeholder="+" aria-label={t("driverForm.dial")} />
						<input
							id={`${idp}-phone`}
							type="tel"
							inputMode="tel"
							value={draft.number}
							onChange={(e) => set("number", e.target.value.replace(/[^\d\s-]/g, ""))}
							className={inputClass(!!problems.number, "tabular-nums")}
						/>
					</div>
				</Field>
				<Field label={t("providerForm.email")} htmlFor={`${idp}-email`} aside={tc("state.optional")} error={problemText(problems.email)}>
					<input id={`${idp}-email`} type="email" value={draft.email} onChange={(e) => set("email", e.target.value)} autoComplete="off" maxLength={TRANSPORT_EMAIL_MAX} className={inputClass(!!problems.email)} />
				</Field>
				<label className="flex cursor-pointer items-start gap-[0.625rem] text-[0.84375rem] text-gms-navy-ink sm:col-span-2">
					<input type="checkbox" className="toggle toggle-sm toggle-primary mt-[0.0625rem]" checked={draft.is_active} onChange={(e) => set("is_active", e.target.checked)} />
					<span>
						<span className="font-medium">{t("providerForm.active")}</span>
						<span className="block text-[0.75rem] text-gms-ink-3">{t("providerForm.activeHint")}</span>
					</span>
				</label>
			</form>
		</Modal>
	);
}
